import React, { useCallback, useEffect, useState } from "react";
import {
  BellRing,
  CalendarClock,
  CalendarOff,
  Clock3,
  RotateCcw,
  Settings2,
  Store,
} from "lucide-react";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { configuracoes } from "../../api/configuracoes";
import { obterEstabelecimentoAtivoId } from "../../utils/estabelecimentoContext";
import { gravarRotuloProfissionalCache } from "../../utils/profissionalRotuloCache";
import ConfiguracaoAgendamento from "./components/ConfiguracaoAgendamento";
import ConfiguracaoNotificacoes from "./components/ConfiguracaoNotificacoes";
import ConfiguracaoHorarios from "./components/ConfiguracaoHorarios";
import ConfiguracaoHorariosAtendimento from "./components/ConfiguracaoHorariosAtendimento";
import ConfiguracaoAgendaExcecoes from "./components/ConfiguracaoAgendaExcecoes";
import "./Configuracoes.css";

const tabs = [
  {
    id: "agendamento",
    label: "Agendamento",
    description: "Regras e automações",
    icon: CalendarClock,
  },
  {
    id: "notificacoes",
    label: "Notificações",
    description: "Canais e lembretes",
    icon: BellRing,
  },
  {
    id: "horarios",
    label: "Funcionamento",
    description: "Abertura e fechamento",
    icon: Store,
  },
  {
    id: "atendimento",
    label: "Disponibilidade",
    description: "Horários para reserva",
    icon: Clock3,
  },
  {
    id: "excecoes",
    label: "Exceções",
    description: "Folgas e feriados",
    icon: CalendarOff,
  },
];

const mensagemErro = (erro, fallback) =>
  erro?.response?.data?.erro ||
  erro?.response?.data?.mensagem ||
  erro?.response?.data?.message ||
  erro?.message ||
  fallback;

const Configuracoes = () => {
  const [estabelecimentoId, setEstabelecimentoId] = useState(() =>
    obterEstabelecimentoAtivoId()
  );
  const [config, setConfig] = useState(null);
  const [aba, setAba] = useState("agendamento");
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const sincronizarEstabelecimento = () => {
      setEstabelecimentoId(obterEstabelecimentoAtivoId());
    };

    sincronizarEstabelecimento();
    window.addEventListener("fz:estabelecimento:changed", sincronizarEstabelecimento);
    return () => {
      window.removeEventListener("fz:estabelecimento:changed", sincronizarEstabelecimento);
    };
  }, []);

  const carregarConfiguracoes = useCallback(async () => {
    if (!estabelecimentoId) {
      setConfig(null);
      setCarregando(false);
      return;
    }

    try {
      setCarregando(true);
      const response = await configuracoes.obter(estabelecimentoId);
      setConfig(response.data);
      if (
        response.data &&
        Object.prototype.hasOwnProperty.call(response.data, "rotulo_profissional")
      ) {
        gravarRotuloProfissionalCache(
          estabelecimentoId,
          response.data.rotulo_profissional,
          { silent: true }
        );
        window.dispatchEvent(
          new CustomEvent("fz:rotulo-profissional:changed", {
            detail: {
              estabelecimentoId: Number(estabelecimentoId),
              rotulo: response.data.rotulo_profissional || null,
            },
          })
        );
      }
    } catch (erro) {
      toast.error(
        mensagemErro(erro, "Não foi possível carregar as configurações."),
        { containerId: "fz-configuracoes", toastId: "fz-config-carregar-erro" }
      );
      console.error(erro);
    } finally {
      setCarregando(false);
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    carregarConfiguracoes();
  }, [carregarConfiguracoes]);

  const handleSalvar = async (dadosAtualizados, opcoes = {}) => {
    if (salvando) return false;

    try {
      setSalvando(true);
      const response = await configuracoes.atualizar(
        estabelecimentoId,
        dadosAtualizados
      );
      const configuracaoAtualizada =
        response.data?.configuracao || response.data;

      setConfig((atual) =>
        configuracaoAtualizada &&
        typeof configuracaoAtualizada === "object"
          ? { ...atual, ...configuracaoAtualizada }
          : { ...atual, ...dadosAtualizados }
      );

      if (Object.prototype.hasOwnProperty.call(dadosAtualizados, "rotulo_profissional")) {
        gravarRotuloProfissionalCache(
          estabelecimentoId,
          configuracaoAtualizada?.rotulo_profissional ??
            dadosAtualizados.rotulo_profissional
        );
      }

      toast.success(opcoes.mensagemSucesso || "Configurações salvas com sucesso.", {
        containerId: "fz-configuracoes",
        toastId: "fz-config-salvo",
        autoClose: 3500,
      });
      window.dispatchEvent(new Event("fz:configuracao:changed"));
      return true;
    } catch (erro) {
      toast.error(
        mensagemErro(erro, "Não foi possível salvar as configurações."),
        {
          containerId: "fz-configuracoes",
          toastId: "fz-config-erro",
          autoClose: 5000,
        }
      );
      console.error(erro);
      return false;
    } finally {
      setSalvando(false);
    }
  };

  const handleRestaurarPadrao = async () => {
    const resultado = await Swal.fire({
      title: "Restaurar configurações?",
      text: "Todas as regras do estabelecimento voltarão aos valores padrão.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Restaurar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ff5a00",
      cancelButtonColor: "#667085",
      reverseButtons: true,
      focusCancel: true,
    });

    if (!resultado.isConfirmed) return;

    try {
      const response =
        await configuracoes.restaurarPadrao(estabelecimentoId);
      setConfig(response.data.configuracao);
      gravarRotuloProfissionalCache(
        estabelecimentoId,
        response.data.configuracao?.rotulo_profissional ?? null
      );
      toast.success("Configurações restauradas para o padrão.", {
        containerId: "fz-configuracoes",
        toastId: "fz-config-restaurado",
      });
      window.dispatchEvent(new Event("fz:configuracao:changed"));
    } catch (erro) {
      toast.error(
        mensagemErro(erro, "Não foi possível restaurar as configurações."),
        { containerId: "fz-configuracoes", toastId: "fz-config-restaurar-erro" }
      );
      console.error(erro);
    }
  };

  if (!estabelecimentoId) {
    return (
      <div className="configuracoes-container">
        <div className="config-empty">
          <Settings2 size={30} />
          <strong>Estabelecimento não identificado</strong>
          <p className="muted">
            Não foi possível determinar o estabelecimento da sua sessão. Faça login
            novamente ou selecione o estabelecimento no painel.
          </p>
        </div>
      </div>
    );
  }

  const toastPortal = (
    <ToastContainer
      containerId="fz-configuracoes"
      position="top-right"
      autoClose={3500}
      newestOnTop
      theme="colored"
      style={{ zIndex: 10000 }}
    />
  );

  if (carregando) {
    return (
      <div className="configuracoes-container loading">
        {toastPortal}
        <span className="config-loading-spinner" />
        Carregando configurações...
      </div>
    );
  }

  if (!config) {
    return (
      <div className="configuracoes-container">
        {toastPortal}
        <div className="config-empty">
          <Settings2 size={30} />
          <strong>Nenhuma configuração encontrada</strong>
          <button type="button" onClick={carregarConfiguracoes}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="configuracoes-container">
      {toastPortal}
      <header className="config-header">
        <div className="config-header__main">
          <div className="config-header__icon">
            <Settings2 size={24} />
          </div>
          <div>
            <span className="config-eyebrow">GESTÃO DA OPERAÇÃO</span>
            <h1>Configurações</h1>
            <p className="config-descricao">
              Defina como a agenda, os avisos e a disponibilidade funcionam.
            </p>
          </div>
        </div>
      </header>

      <nav className="config-tabs" aria-label="Seções de configuração">
        {tabs.map(({ id, label, description, icon: Icon }) => (
          <button
            type="button"
            key={id}
            className={`tab-button ${aba === id ? "ativo" : ""}`}
            onClick={() => setAba(id)}
            aria-current={aba === id ? "page" : undefined}
          >
            <Icon size={19} />
            <span>
              <strong>{label}</strong>
              <small>{description}</small>
            </span>
          </button>
        ))}
      </nav>

      <div className="config-flow-note">
        <CalendarClock size={18} />
        <span>
          Primeiro defina o <strong>funcionamento</strong>; depois escolha os
          horários que poderão ser reservados em{" "}
          <strong>disponibilidade</strong>.
        </span>
      </div>

      <main className="config-content">
        {aba === "agendamento" && (
          <ConfiguracaoAgendamento
            config={config}
            estabelecimentoId={estabelecimentoId}
            onSalvar={handleSalvar}
            salvando={salvando}
          />
        )}
        {aba === "notificacoes" && (
          <ConfiguracaoNotificacoes
            config={config}
            onSalvar={handleSalvar}
            salvando={salvando}
          />
        )}
        {aba === "horarios" && (
          <ConfiguracaoHorarios
            config={config}
            onSalvar={handleSalvar}
            salvando={salvando}
            estabelecimentoId={estabelecimentoId}
          />
        )}
        {aba === "atendimento" && (
          <ConfiguracaoHorariosAtendimento
            estabelecimentoId={estabelecimentoId}
            config={config}
          />
        )}
        {aba === "excecoes" && (
          <ConfiguracaoAgendaExcecoes estabelecimentoId={estabelecimentoId} />
        )}
      </main>

      <details className="config-advanced">
        <summary>Opções avançadas</summary>
        <div>
          <p>
            Use esta opção somente quando precisar recomeçar a configuração do
            estabelecimento.
          </p>
          <button
            type="button"
            className="btn-restaurar"
            onClick={handleRestaurarPadrao}
          >
            <RotateCcw size={17} />
            Restaurar valores padrão
          </button>
        </div>
      </details>
    </div>
  );
};

export default Configuracoes;
